'use client';

import { motion } from 'motion/react';
import { fade, fadeUp, staggerParent } from '@/lib/motion/tokens';

/**
 * Entrance primitives.
 *
 * These exist so that "a thing appears" is one decision made once, rather than
 * forty call sites each picking their own distance, duration and delay. Three
 * components cover every case in the dashboard:
 *
 *   <Reveal>            one element, on mount
 *   <Stagger><StaggerItem>…   a list or grid, in sequence
 *   <Reveal whenInView> below the fold, on scroll
 *
 * All of them are plain wrappers around a Motion element, so `className` and
 * any DOM prop pass straight through and they can replace a <div> in place.
 */

/**
 * @param {boolean} whenInView  Animate when scrolled into view rather than on
 *                              mount. Fires once; a section that re-animates
 *                              every time you scroll past it is a distraction
 *                              in a tool people scroll all day.
 * @param {number}  delay       Seconds. Use sparingly — prefer <Stagger>.
 * @param {boolean} move        false → fade only, no travel. For content that
 *                              sits in a fixed grid slot where 8px of movement
 *                              would visibly nudge its neighbours.
 */
export default function Reveal({
  as = 'div',
  whenInView = false,
  delay = 0,
  move = true,
  className,
  children,
  ...props
}) {
  const Tag = motion[as] || motion.div;
  const variants = move ? fadeUp : fade;

  const trigger = whenInView
    ? { whileInView: 'visible', viewport: { once: true, amount: 0.2 } }
    : { animate: 'visible' };

  return (
    <Tag
      initial="hidden"
      {...trigger}
      variants={variants}
      transition={delay ? { ...variants.visible.transition, delay } : undefined}
      className={className}
      {...props}
    >
      {children}
    </Tag>
  );
}

/**
 * Sequences its children. Children must be <StaggerItem> (or any Motion
 * element declaring `hidden`/`visible`) — Motion propagates the animation
 * state down the tree, so items carry no delay of their own and the list can
 * be filtered or reordered without re-tuning anything.
 *
 * @param {number} gap    Seconds between children.
 * @param {number} delay  Seconds before the first child.
 */
export function Stagger({
  as = 'div',
  gap = 0.035,
  delay = 0,
  whenInView = false,
  className,
  children,
  ...props
}) {
  const Tag = motion[as] || motion.div;

  const trigger = whenInView
    ? { whileInView: 'visible', viewport: { once: true, amount: 0.15 } }
    : { animate: 'visible' };

  return (
    <Tag
      initial="hidden"
      {...trigger}
      variants={staggerParent(gap, delay)}
      className={className}
      {...props}
    >
      {children}
    </Tag>
  );
}

/** A single item inside <Stagger>. */
export function StaggerItem({ as = 'div', move = true, className, children, ...props }) {
  const Tag = motion[as] || motion.div;

  return (
    <Tag variants={move ? fadeUp : fade} className={className} {...props}>
      {children}
    </Tag>
  );
}
